import React from 'react';

import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  Stack,
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

interface BuyPlanPromptProps {
  title?: string;
  description?: string;
  actionText?: string;
  actionHref?: string;
  secondaryAction?: React.ReactNode;
  sx?: object;
}

export function BuyPlanPrompt({
  title,
  description,
  actionText,
  actionHref = paths.setting.billing.root,
  secondaryAction,
  sx,
}: BuyPlanPromptProps) {
  const { t } = useTranslate('translation');
  const theme = useTheme();

  const displayTitle =
    title ||
    t('ADDING_CHANNEL_REQUIRES_PAID_USER', {
      defaultValue: 'Adding a channel requires a paid user.',
    });

  const displayDescription =
    description ||
    t('BUY_PLAN_TO_ACTIVATE_FEATURES', {
      defaultValue: 'Buy a plan to activate channels, campaigns, workflows and AI agents.',
    });

  const displayActionText =
    actionText ||
    t('VIEW_PLANS', {
      defaultValue: 'View plans',
    });

  return (
    <Card
      sx={{
        p: { xs: 4, sm: 6 },
        borderRadius: 3,
        textAlign: 'center',
        maxWidth: 580,
        mx: 'auto',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: (themeArg) =>
          themeArg.palette.mode === 'dark'
            ? `0px 24px 48px -12px rgba(0, 0, 0, 0.6), 0px 0px 32px ${alpha(themeArg.palette.primary.main, 0.1)}`
            : `0px 24px 48px -12px ${alpha(themeArg.palette.primary.main, 0.12)}, 0px 0px 32px ${alpha(themeArg.palette.primary.main, 0.06)}`,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <Iconify icon="solar:cart-large-4-bold-duotone" width={44} />
      </Box>

      <Typography variant="h4" sx={{ mb: 1.5, fontWeight: 700 }}>
        {displayTitle}
      </Typography>

      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
        {displayDescription}
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="center"
        alignItems="center"
      >
        <Button
          component={RouterLink}
          href={actionHref}
          variant="contained"
          size="large"
          color="primary"
          startIcon={<Iconify icon="solar:card-outline" />}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: '50px',
            fontWeight: 700,
          }}
        >
          {displayActionText}
        </Button>

        {secondaryAction}
      </Stack>
    </Card>
  );
}

interface BuyPlanPromptDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

export function BuyPlanPromptDialog({
  open,
  onClose,
  title,
  description,
}: BuyPlanPromptDialogProps) {
  const { t } = useTranslate('translation');

  const displayTitle =
    title ||
    t('ADDING_CHANNEL_REQUIRES_PAID_USER', {
      defaultValue: 'Adding a channel requires a paid user.',
    });

  const displayDescription =
    description ||
    t('BUY_PLAN_TO_ACTIVATE_FEATURES', {
      defaultValue: 'Buy a plan to activate channels, campaigns, workflows and AI agents.',
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Iconify icon="solar:info-circle-bold" width={24} sx={{ color: 'primary.main' }} />
        {displayTitle}
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {displayDescription}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.5 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" sx={{ px: 2.5, py: 0.85 }}>
          {t('CLOSE', { defaultValue: 'Close' })}
        </Button>
        <Button
          component={RouterLink}
          href={paths.setting.billing.root}
          variant="contained"
          color="primary"
          onClick={onClose}
          sx={{ px: 2.5, py: 0.85 }}
        >
          {t('VIEW_PLANS', { defaultValue: 'View plans' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
